import React, { useState, useEffect, useRef, useCallback } from 'react';
import { RedeemParticipant, UnredeemParticipant, PlayXtpAudio } from '../../../../utilities/BL/Xtp';

export const XtpCheckinModal = ({ isOpen, onClose, onRedeemSuccess }) => {
    // Estado del proceso de canje: 'IDLE' | 'VALIDATING' | 'SUCCESS' | 'ALREADY_REDEEMED' | 'DISQUALIFIED' | 'INVALID' | 'ERROR'
    const [status, setStatus] = useState('IDLE');
    const [resultData, setResultData] = useState(null);
    const [errorMessage, setErrorMessage] = useState('');

    // Búsqueda manual estricta
    const [manualCode, setManualCode] = useState('');
    const inputRef = useRef(null);

    // Buffer y marcas de tiempo para lector de código de barras / QR USB Keyboard Wedge
    const keyBufferRef = useRef('');
    const lastKeyTimeRef = useRef(0);

    // Operador y ubicación
    const [operatorName] = useState(() => {
        return sessionStorage.getItem('xtp_staff_operator_name') || 'Staff Operativo';
    });
    const [sourceLocation] = useState(() => {
        return sessionStorage.getItem('xtp_staff_location') || 'xolo_shop_local';
    });

    // Regresar al modo listo para escanear
    const resetToIdle = useCallback(() => {
        setStatus('IDLE');
        setResultData(null);
        setErrorMessage('');
        setManualCode('');

        // Devolver el foco al input de respaldo
        setTimeout(() => {
            if (inputRef.current) {
                inputRef.current.focus();
            }
        }, 100);
    }, []);

    // Revertir canje de participante con confirmación explícita (Modo Pruebas / Corrección)
    const handleRevertRedeem = async () => {
        if (!resultData) return;
        const confirmMsg = `¿Confirmar reversión de canje para ${resultData.name || 'el participante'} (Folio: ${resultData.passCode})?\n\nEl pase volverá a estar PENDIENTE y podrá ser canjeado nuevamente.`;
        if (window.confirm(confirmMsg)) {
            try {
                const res = await UnredeemParticipant({
                    participantId: resultData.id,
                    passCode: resultData.passCode,
                    operatorName: `${operatorName} (Reversión Modal)`
                });
                if (res && res.success) {
                    PlayXtpAudio('success');
                    if (onRedeemSuccess) {
                        onRedeemSuccess(resultData);
                    }
                    resetToIdle();
                } else {
                    alert(res?.message || 'Error al revertir el canje.');
                }
            } catch (err) {
                console.error('Error al revertir canje:', err);
                alert('Error de conexión al revertir el canje.');
            }
        }
    };

    // Procesar la transacción de canje
    const handleProcessRedeem = async (input) => {
        if (status === 'VALIDATING') return;

        setStatus('VALIDATING');
        setErrorMessage('');
        setResultData(null);

        const payload = {
            operatorName: operatorName || 'Staff Operativo',
            source: sourceLocation || 'xolo_shop_local'
        };

        let raw = (input.token || input.passCode || input.medalCode || input.rawText || '').trim();

        // 1. Limpieza de prefijos emitidos por escáneres USB/Bluetooth (ej: "c3", "]Q3", etc.)
        if (raw.toUpperCase().includes('TPX26-')) {
            const idx = raw.toUpperCase().indexOf('TPX26-');
            raw = raw.substring(idx);
            if (!raw.startsWith('#')) {
                raw = '#' + raw;
            }
        }

        // 2. Si contiene un punto '.', o inicia con 'ey' (token Base64 heredado), o es una cadena criptográfica larga
        if (raw.includes('.') || raw.startsWith('eyJ') || raw.startsWith('ey') || (raw.length > 40 && !raw.includes(' '))) {
            payload.token = raw;
        } else {
            // Es un folio manual (ej: "H4EC4", "TPX26-H4EC4", "#TPX26-H4EC4")
            payload.passCode = raw;
        }

        try {
            const res = await RedeemParticipant(payload);

            if (res && res.success && res.status === 'REDEEMED_SUCCESS') {
                setStatus('SUCCESS');
                setResultData(res.participant);
                PlayXtpAudio('success');

                if (onRedeemSuccess) {
                    onRedeemSuccess(res.participant);
                }
                // La pantalla queda fija indefinidamente permitiendo escaneo continuo del siguiente aficionado

            } else if (res && res.status === 'ALREADY_REDEEMED') {
                setStatus('ALREADY_REDEEMED');
                setResultData(res.participant);
                setErrorMessage(res.message || 'Este beneficio ya fue entregado previamente.');
                PlayXtpAudio('warning');

            } else if (res && res.status === 'DISQUALIFIED') {
                setStatus('DISQUALIFIED');
                setResultData(res.participant);
                setErrorMessage(res.message || 'El pase se encuentra descalificado.');
                PlayXtpAudio('error');

            } else if (res && res.status === 'INVALID_SIGNATURE') {
                setStatus('INVALID');
                setErrorMessage(res.message || 'Código QR inválido o firma criptográfica alterada.');
                PlayXtpAudio('error');

            } else {
                setStatus('INVALID');
                setErrorMessage(res?.message || 'No se encontró ningún pase activo con el código ingresado.');
                PlayXtpAudio('error');
            }
        } catch (err) {
            console.error('Error al procesar canje:', err);
            setStatus('ERROR');
            setErrorMessage('Error de conexión al validar con el servidor. Intenta nuevamente.');
            PlayXtpAudio('error');
        }
    };

    // Listener Keyboard Wedge para pistolas de código de barras USB/Bluetooth y tecla Espacio
    useEffect(() => {
        if (!isOpen) return;

        const handleGlobalKeyDown = (e) => {
            // Tecla Espacio durante pantalla de éxito para saltar inmediatamente el auto-retorno
            if (e.key === ' ' || e.code === 'Space') {
                if (status === 'SUCCESS') {
                    e.preventDefault();
                    resetToIdle();
                    return;
                }
            }

            // Tecla Escape para cerrar o volver a modo escáner
            if (e.key === 'Escape') {
                if (status !== 'IDLE') {
                    e.preventDefault();
                    resetToIdle();
                    return;
                }
            }

            // Detección de caracteres para Keyboard Wedge
            const now = Date.now();
            const timeDiff = now - lastKeyTimeRef.current;
            lastKeyTimeRef.current = now;

            // Las pistolas emiten caracteres a muy alta velocidad (<50ms entre pulsaciones)
            if (e.key === 'Enter') {
                const scannedBuffer = keyBufferRef.current.trim();
                keyBufferRef.current = '';

                if (scannedBuffer.length >= 4) {
                    e.preventDefault();
                    handleProcessRedeem({ token: scannedBuffer, rawText: scannedBuffer });
                }
            } else if (e.key.length === 1) {
                // Si pasó más de 80ms, reiniciamos buffer porque probablemente es tecleo manual
                if (timeDiff > 80) {
                    keyBufferRef.current = e.key;
                } else {
                    keyBufferRef.current += e.key;
                }
            }
        };

        window.addEventListener('keydown', handleGlobalKeyDown);
        return () => {
            window.removeEventListener('keydown', handleGlobalKeyDown);
        };
    }, [isOpen, status, resetToIdle]);

    // Autofocus en el input al abrir el modal
    useEffect(() => {
        if (isOpen) {
            setTimeout(() => {
                if (inputRef.current) {
                    inputRef.current.focus();
                }
            }, 200);
        }
    }, [isOpen]);

    // Formateador de fecha amigable para México
    const formatDate = (isoStr) => {
        if (!isoStr) return 'N/A';
        try {
            return new Date(isoStr).toLocaleString('es-MX', {
                timeZone: 'America/Tijuana',
                day: '2-digit',
                month: 'short',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
                hour12: true
            });
        } catch {
            return isoStr;
        }
    };

    if (!isOpen) return null;

    return (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(5, 7, 13, 0.85)', zIndex: 1060 }} tabIndex="-1">
            <div className="modal-dialog modal-dialog-centered modal-lg">
                <div className="modal-content border-0 shadow-lg rounded-4 overflow-hidden bg-white">
                    {/* ENCABEZADO DEL MODAL */}
                    <div className="modal-header bg-dark text-white border-bottom border-danger border-3 px-4 py-3">
                        <div className="d-flex align-items-center gap-2">
                            <span className="badge bg-danger p-2 rounded-circle">
                                <i className="bi bi-upc-scan fs-5 text-white"></i>
                            </span>
                            <div>
                                <h5 className="modal-title fw-black mb-0 text-white text-uppercase tracking-wider">
                                    Validar y Canjear Pase (Staff)
                                </h5>
                                <div className="d-flex align-items-center gap-2 small text-muted">
                                    <span>Punto: <strong>{sourceLocation === 'xolo_shop_local' ? 'Xolo Shop Tienda' : 'Estadio Caliente'}</strong></span>
                                    <span>•</span>
                                    <span>Operador: <strong>{operatorName}</strong></span>
                                </div>
                            </div>
                        </div>
                        <button
                            type="button"
                            className="btn-close btn-close-white"
                            onClick={onClose}
                            aria-label="Close"
                        ></button>
                    </div>

                    {/* CUERPO DEL MODAL */}
                    <div className="modal-body p-4">
                        {/* ============================================================== */}
                        {/* CASO 1: VALIDANDO */}
                        {/* ============================================================== */}
                        {status === 'VALIDATING' && (
                            <div className="text-center py-5">
                                <div className="spinner-border text-danger mb-3" style={{ width: '3.8rem', height: '3.8rem' }} role="status">
                                    <span className="visually-hidden">Validando...</span>
                                </div>
                                <h3 className="fw-black text-dark mb-2">Validando...</h3>
                                <p className="text-muted fs-6">Consultando el folio y verificando el beneficio en tiempo real, por favor espera un momento.</p>
                            </div>
                        )}

                        {/* ============================================================== */}
                        {/* CASO 2: ÉXITO (PANTALLA VERDE - BENEFICIO ENTREGADO) */}
                        {/* ============================================================== */}
                        {status === 'SUCCESS' && resultData && (
                            <div className="text-center py-4 px-3 bg-success bg-opacity-10 border border-success border-2 rounded-4">
                                <div className="mb-3">
                                    <i className={`bi ${resultData.isWinner ? 'bi-trophy-fill text-warning' : 'bi-check-circle-fill text-success'}`} style={{ fontSize: '4.5rem' }}></i>
                                </div>
                                {resultData.isWinner ? (
                                    <div className="alert border-2 border-warning bg-warning bg-opacity-25 py-3 px-4 mb-3 rounded-4 shadow-sm text-center">
                                        <div className="d-flex align-items-center justify-content-center gap-3">
                                            <span className="fs-1">🏆</span>
                                            <div>
                                                <div className="fw-black text-danger text-uppercase fs-5 tracking-wide">
                                                    🎁 ¡ENTREGAR PREMIO ESPECIAL AL AFICIONADO!
                                                </div>
                                                <div className="fw-black text-dark fs-4 mt-1">
                                                    {resultData.prizeName || 'Premio Conmemorativo Oficial'}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                ) : (
                                    <span className="badge bg-success text-uppercase px-3 py-2 fs-6 fw-bold mb-2">
                                        ✅ BENEFICIO ENTREGADO EXITOSAMENTE
                                    </span>
                                )}
                                <h3 className="fw-black text-dark mb-1 mt-2 text-uppercase">
                                    {resultData.name}
                                </h3>

                                <div className="row g-2 justify-content-center my-3 max-w-md mx-auto">
                                    <div className="col-6 col-sm-4">
                                        <div className="bg-white p-2 rounded-3 shadow-sm border">
                                            <div className="small text-muted text-uppercase fw-bold" style={{ fontSize: '0.7rem' }}>Folio Cupón</div>
                                            <div className="fw-black text-danger">{resultData.passCode}</div>
                                        </div>
                                    </div>
                                    <div className="col-6 col-sm-4">
                                        <div className="bg-white p-2 rounded-3 shadow-sm border">
                                            <div className="small text-muted text-uppercase fw-bold" style={{ fontSize: '0.7rem' }}># Medalla</div>
                                            <div className="fw-black text-dark">{resultData.medalCode}</div>
                                        </div>
                                    </div>
                                    <div className="col-12 col-sm-4">
                                        <div className="bg-white p-2 rounded-3 shadow-sm border">
                                            <div className="small text-muted text-uppercase fw-bold" style={{ fontSize: '0.7rem' }}># Ticket</div>
                                            <div className="fw-black text-dark">{resultData.ticketNumber}</div>
                                        </div>
                                    </div>
                                </div>

                                {/* Indicador de escaneo continuo y botón para siguiente */}
                                <div className="mt-4 pt-2">
                                    <div className="badge bg-success bg-opacity-25 text-success border border-success border-opacity-25 px-3 py-2 rounded-pill mb-3 small fw-bold">
                                        <i className="bi bi-qr-code-scan me-1"></i> Listo para el siguiente aficionado: escanea su código QR directamente
                                    </div>
                                    <div>
                                        <button
                                            type="button"
                                            className="btn btn-success fw-bold px-4 py-2 shadow-sm rounded-pill"
                                            onClick={resetToIdle}
                                        >
                                            <i className="bi bi-arrow-repeat me-1"></i> Escanear Otro (Espacio)
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* ============================================================== */}
                        {/* CASO 3: BENEFICIO YA ENTREGADO (PANTALLA AMARILLA) */}
                        {/* ============================================================== */}
                        {status === 'ALREADY_REDEEMED' && (
                            <div className="text-center py-4 px-3 bg-warning bg-opacity-10 border border-warning border-2 rounded-4">
                                <div className="mb-3">
                                    <i className="bi bi-exclamation-triangle-fill text-warning" style={{ fontSize: '4.5rem' }}></i>
                                </div>
                                <span className="badge bg-warning text-dark text-uppercase px-3 py-2 fs-6 fw-bold mb-2">
                                    ⚠️ BENEFICIO YA ENTREGADO PREVIAMENTE
                                </span>
                                <h4 className="fw-bold text-dark mt-2 mb-2">
                                    {resultData?.name || 'Aficionado Registrado'}
                                </h4>
                                {resultData?.isWinner && (
                                    <div className="badge bg-warning text-dark border border-warning px-3 py-1 fs-6 fw-bold mb-2 d-inline-block">
                                        🏆 AFICIONADO GANADOR: {resultData.prizeName || 'Premio Especial Oficial'}
                                    </div>
                                )}
                                <div className="alert alert-warning d-inline-block text-start small mb-3 shadow-sm">
                                    <div><strong>Folio:</strong> {resultData?.passCode} • <strong>Medalla:</strong> {resultData?.medalCode}</div>
                                    <div><strong>Entregado el:</strong> {formatDate(resultData?.redeemedAtUtc)}</div>
                                    <div><strong>Registrado por:</strong> {resultData?.redeemedBy || 'Staff Operativo'}</div>
                                </div>
                                <div className="d-flex align-items-center justify-content-center gap-2">
                                    <button
                                        type="button"
                                        className="btn btn-dark fw-bold px-4 py-2 rounded-pill"
                                        onClick={resetToIdle}
                                    >
                                        <i className="bi bi-arrow-left me-1"></i> Volver a Escanear
                                    </button>
                                    <button
                                        type="button"
                                        className="btn btn-outline-danger fw-bold px-3 py-2 rounded-pill"
                                        onClick={handleRevertRedeem}
                                        title="Revertir canje para pruebas (Requiere Confirmación)"
                                    >
                                        <i className="bi bi-arrow-counterclockwise me-1"></i> Revertir Canje (Pruebas)
                                    </button>
                                </div>
                            </div>
                        )}

                        {/* ============================================================== */}
                        {/* CASO 4: PASE DESCALIFICADO (PANTALLA ROJA) */}
                        {/* ============================================================== */}
                        {status === 'DISQUALIFIED' && (
                            <div className="text-center py-4 px-3 bg-danger bg-opacity-10 border border-danger border-2 rounded-4">
                                <div className="mb-3">
                                    <i className="bi bi-slash-circle-fill text-danger" style={{ fontSize: '4.5rem' }}></i>
                                </div>
                                <span className="badge bg-danger text-white text-uppercase px-3 py-2 fs-6 fw-bold mb-2">
                                    ❌ PASE DESCALIFICADO
                                </span>
                                <h4 className="fw-bold text-dark mt-2 mb-1">
                                    {resultData?.name || 'Titular'}
                                </h4>
                                <div className="alert alert-danger d-inline-block text-start small mb-3 shadow-sm">
                                    <div><strong>Motivo:</strong> {resultData?.disqualifiedReason || 'Descalificado por administración'}</div>
                                    <div><strong>Descalificado por:</strong> {resultData?.disqualifiedBy || 'Staff Admin'}</div>
                                    <div><strong>Fecha:</strong> {formatDate(resultData?.disqualifiedAtUtc)}</div>
                                </div>
                                <div>
                                    <button
                                        type="button"
                                        className="btn btn-dark fw-bold px-4 py-2 rounded-pill"
                                        onClick={resetToIdle}
                                    >
                                        <i className="bi bi-arrow-left me-1"></i> Volver a Escanear
                                    </button>
                                </div>
                            </div>
                        )}

                        {/* ============================================================== */}
                        {/* CASO 5: INVÁLIDO O ERROR (PANTALLA ROJA) */}
                        {/* ============================================================== */}
                        {(status === 'INVALID' || status === 'ERROR') && (
                            <div className="text-center py-4 px-3 bg-danger bg-opacity-10 border border-danger border-2 rounded-4">
                                <div className="mb-3">
                                    <i className="bi bi-x-circle-fill text-danger" style={{ fontSize: '4.5rem' }}></i>
                                </div>
                                <span className="badge bg-danger text-white text-uppercase px-3 py-2 fs-6 fw-bold mb-2">
                                    ❌ {status === 'INVALID' ? 'PASE INVÁLIDO O NO ENCONTRADO' : 'ERROR DE CONEXIÓN'}
                                </span>
                                <p className="text-danger fw-bold my-3 fs-5">
                                    {errorMessage}
                                </p>
                                <div>
                                    <button
                                        type="button"
                                        className="btn btn-dark fw-bold px-4 py-2 rounded-pill"
                                        onClick={resetToIdle}
                                    >
                                        <i className="bi bi-arrow-left me-1"></i> Reintentar Escaneo
                                    </button>
                                </div>
                            </div>
                        )}

                        {/* ============================================================== */}
                        {/* CASO 6: LISTO PARA ESCANEAR (IDLE) */}
                        {/* ============================================================== */}
                        {status === 'IDLE' && (
                            <div>
                                {/* INDICACIONES CLARAS Y SENCILLAS */}
                                <div className="card border-0 bg-dark text-white rounded-4 p-4 text-center mb-4 shadow-sm">
                                    <h5 className="fw-bold text-white mb-0">
                                        Acerca el código QR de la persona al escáner.
                                    </h5>
                                </div>

                                {/* BÚSQUEDA MANUAL */}
                                <div className="card border-0 bg-light rounded-4 p-4 shadow-sm">
                                    <form
                                        onSubmit={(e) => {
                                            e.preventDefault();
                                            if (manualCode.trim()) {
                                                handleProcessRedeem({ rawText: manualCode.trim() });
                                            }
                                        }}
                                    >
                                        <label className="form-label fw-bold text-dark mb-2 fs-6">
                                            Escribe el folio:
                                        </label>
                                        <div className="mb-3">
                                            <input
                                                ref={inputRef}
                                                type="text"
                                                className="form-control form-control-lg bg-white fw-bold fs-5 text-uppercase shadow-sm"
                                                placeholder="Ej. H4EC4 o #TPX26-H4EC4"
                                                value={manualCode}
                                                onChange={(e) => setManualCode(e.target.value)}
                                                autoFocus
                                            />
                                        </div>
                                        <button
                                            type="submit"
                                            className="btn btn-danger btn-lg w-100 fw-bold text-uppercase py-3 rounded-3 shadow-sm d-flex align-items-center justify-content-center gap-2"
                                            disabled={!manualCode.trim() || status === 'VALIDATING'}
                                            style={{ backgroundColor: '#dc2626', borderColor: '#dc2626' }}
                                        >
                                            <i className="bi bi-check-circle-fill"></i>
                                            <span>Validar folio</span>
                                        </button>
                                    </form>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* PIE DEL MODAL */}
                    <div className="modal-footer bg-light px-4 py-2 d-flex justify-content-end">
                        <button
                            type="button"
                            className="btn btn-secondary btn-sm px-4 rounded-pill fw-semibold"
                            onClick={onClose}
                        >
                            Cerrar
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};
